import {useDisclosure} from '@mantine/hooks';
import React, {useImperativeHandle, useState} from 'react';

import {AntibodyEnforcedSchemaSpec} from 'antibodies/schema-configuration/AntibodyEnforcedDataModelConstants';
import {
  AntibodyComponentSchemaInterfaceId,
  ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME,
} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';

import AntibodyComponentMigrationPreviewModal from './AntibodyComponentMigrationPreviewModal';

export interface AntibodyComponentMigrationControllerHandle {
  open: (spec: AntibodyEnforcedSchemaSpec, interfaceId: AntibodyComponentSchemaInterfaceId) => void;
}

export interface AntibodyComponentMigrationControllerProps {}

/**
 * Encapsulates the migration preview modal and its open/pending state so that
 * TagSchemaFileSettings (a large class component) can trigger the flow with a
 * single imperative `ref.current.open(spec, interfaceId)` call rather than
 * managing modal state itself.
 */
const AntibodyComponentMigrationController = React.forwardRef<
  AntibodyComponentMigrationControllerHandle,
  AntibodyComponentMigrationControllerProps
>((props, ref) => {
  const [opened, {open: openDisclosure, close}] = useDisclosure(false);
  const [pendingSpec, setPendingSpec] = useState<AntibodyEnforcedSchemaSpec | null>(null);
  const [pendingInterfaceId, setPendingInterfaceId] = useState<AntibodyComponentSchemaInterfaceId | null>(
    null
  );

  useImperativeHandle(ref, () => ({
    open(spec, interfaceId) {
      setPendingSpec(spec);
      setPendingInterfaceId(interfaceId);
      openDisclosure();
    },
  }));

  if (!opened || pendingSpec == null || pendingInterfaceId == null) {
    return null;
  }

  const componentTypeName = ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME[pendingInterfaceId];
  return (
    <AntibodyComponentMigrationPreviewModal
      componentTypeName={componentTypeName}
      onClose={close}
      onApply={close}
    />
  );
});

export default AntibodyComponentMigrationController;
